/**
 * Gravity Assist Physics Engine
 * Implements hyperbolic orbital mechanics for gravity assist maneuvers
 */

// Core physics types
export interface Vector2D {
  x: number;
  y: number;
}

export interface PhysicsInputs {
  mu: number;        // Gravitational parameter (km³/s²)
  v0: number;        // Planet orbital speed (km/s)
  v: number;         // Probe approach speed (km/s)
  theta: number;     // Approach angle (degrees)
  b: number;         // Impact parameter (km)
  Rpl?: number;      // Planet radius (km) - optional
  zoom?: number;     // Zoom level for visualization
  maxDist?: number;  // Maximum distance for trajectory generation
}

export interface TrajectoryPoint {
  F: number;         // Hyperbolic anomaly
  t: number;         // Time from periapsis (s)
  r: number;         // Distance from planet (km)
  x_P: number;       // Position in planet frame (km)
  y_P: number;
  u_x: number;       // Velocity in planet frame (km/s)
  u_y: number;
  u_mag: number;     // Speed magnitude (km/s)
}

export interface Warning {
  type: 'error' | 'warning' | 'info';
  msg: string;
}

export interface PhysicsState {
  // Input parameters
  mu: number;
  v0: number;
  v: number;
  theta: number;
  b: number;
  Rpl?: number;
  zoom?: number;

  // Velocity vectors
  v_vec: Vector2D;      // Probe velocity in sun frame
  Vp_vec: Vector2D;     // Planet velocity in sun frame
  u_in: Vector2D;       // Relative velocity (incoming)
  u_out: Vector2D;      // Relative velocity (outgoing)
  v_out: Vector2D;      // Probe velocity in sun frame (outgoing)

  // Derived quantities
  U: number;            // Relative speed (km/s)
  gamma_in: number;     // Incoming angle in planet frame (rad)
  s: number;            // Sign of impact parameter (+1 or -1)
  delta: number;        // Scattering angle (rad)
  deltaRad: number;     // Scattering angle (rad) - alias

  // Orbital parameters
  A: number;            // Semi-major axis magnitude (km)
  a: number;            // Semi-major axis (negative for hyperbola)
  h: number;            // Specific angular momentum
  e: number;            // Eccentricity
  p: number;            // Semi-latus rectum (km)
  rp: number;           // Periapsis distance (km)

  // Orientation
  omega: number;        // Argument of periapsis (rad)

  // Results
  v_in_mag: number;     // Incoming speed in sun frame (km/s)
  v_out_mag: number;    // Outgoing speed in sun frame (km/s)
  deltaE: number;       // Specific energy change (km²/s²)

  // Trajectory data
  trajectory: TrajectoryPoint[];

  // Status
  warnings: Warning[];
}

export class GravityAssistPhysics {
  private readonly DEG_TO_RAD: number = Math.PI / 180;

  /**
   * Main computation function
   * Calculates complete gravity assist trajectory and results
   */
  compute(inputs: PhysicsInputs): PhysicsState {
    const { mu, v0, v, theta, b, Rpl } = inputs;

    // Convert theta to radians
    const thetaRad = theta * this.DEG_TO_RAD;

    // Validation
    if (mu <= 0) {
      throw new Error("μ must be positive");
    }

    // § 2: Probe velocity in Sun frame
    const v_vec: Vector2D = {
      x: v * Math.cos(thetaRad),
      y: v * Math.sin(thetaRad)
    };

    // Planet velocity in Sun frame
    const Vp_vec: Vector2D = { x: v0, y: 0 };

    // § 2: Relative velocity (approach velocity in planet frame)
    const u_in: Vector2D = {
      x: v_vec.x - Vp_vec.x,
      y: v_vec.y - Vp_vec.y
    };

    // Relative speed
    const U = Math.sqrt(u_in.x * u_in.x + u_in.y * u_in.y);

    // Check for undefined scatter
    if (U < 1e-6) {
      throw new Error("U ≈ 0: undefined scatter (probe matches planet velocity)");
    }

    // Incoming angle in planet frame
    const gamma_in = Math.atan2(u_in.y, u_in.x);

    // Sign of impact parameter (determines left/right turn)
    const s = Math.sign(b);

    // § 3.1: Scattering angle from impact parameter
    const delta = 2 * Math.atan2(mu, Math.abs(b) * U * U);

    // § 3.2 & 3.3: Conic parameters
    const A = mu / (U * U); // |a|
    const a = -A;
    const h = Math.abs(b) * U;
    const e = Math.sqrt(1 + (b * b * U * U * U * U) / (mu * mu));
    const p = h * h / mu;
    const rp = A * (e - 1);

    if (rp <= 0) {
      throw new Error("r_p ≤ 0: invalid trajectory");
    }

    // § 4.1: Outgoing velocity in planet frame (rotation by s*delta)
    const cos_delta = Math.cos(s * delta);
    const sin_delta = Math.sin(s * delta);
    const u_out: Vector2D = {
      x: u_in.x * cos_delta - u_in.y * sin_delta,
      y: u_in.x * sin_delta + u_in.y * cos_delta
    };

    // § 4.2: Outgoing velocity in Sun frame
    const v_out: Vector2D = {
      x: u_out.x + Vp_vec.x,
      y: u_out.y + Vp_vec.y
    };

    // § 4.3: Energy change
    const v_in_mag = Math.sqrt(v_vec.x * v_vec.x + v_vec.y * v_vec.y);
    const v_out_mag = Math.sqrt(v_out.x * v_out.x + v_out.y * v_out.y);
    const deltaE = 0.5 * (v_out_mag * v_out_mag - v_in_mag * v_in_mag);

    // § 5.1: Orientation of the orbit (argument of periapsis)
    const omega = gamma_in - s * (Math.PI / 2 - delta / 2);

    // Pre-compute rotation matrix for omega
    const cos_omega = Math.cos(omega);
    const sin_omega = Math.sin(omega);

    // § 5: Generate trajectory points
    const trajectory = this.generateTrajectory({
      A, e, rp, mu,
      omega, cos_omega, sin_omega,
      u_in, b
    }, inputs.maxDist);

    return {
      // Input parameters
      mu, v0, v, theta, b, Rpl, zoom: inputs.zoom,

      // Derived quantities
      v_vec, Vp_vec, u_in, u_out, v_out,
      U, gamma_in, s, delta, deltaRad: delta,

      // Conic parameters
      A, a, h, e, p, rp,

      // Orientation
      omega,

      // Results
      v_in_mag, v_out_mag, deltaE,

      // Trajectory data
      trajectory,

      // Status
      warnings: this.checkWarnings(U, rp, Rpl, delta)
    };
  }

  /**
   * § 5.2: Generate hyperbolic trajectory points in planet frame
   * Uses hyperbolic anomaly parameterization
   */
  private generateTrajectory(params: {
    A: number;
    e: number;
    mu: number;
    omega: number;
    cos_omega: number;
    sin_omega: number;
    u_in: Vector2D;
    rp: number;
    b: number;
  }, maxDist?: number): TrajectoryPoint[] {
    const { A, e, mu, cos_omega, sin_omega } = params;

    // Choose F range based on distance cap
    const r_max = maxDist || Math.max(20 * params.rp, 1e5);
    const F_max = Math.acosh((1 + r_max / A) / e);
    const F_min = -F_max;

    // Sign of impact parameter determines trajectory orientation
    const s = Math.sign(params.b || 1);

    // Sample 300 points along the trajectory
    const numPoints = 300;
    const points: TrajectoryPoint[] = [];

    for (let i = 0; i < numPoints; i++) {
      const F = F_min + (F_max - F_min) * i / (numPoints - 1);

      // § 5.2: Hyperbolic anomaly parameterization
      const cosh_F = Math.cosh(F);
      const sinh_F = Math.sinh(F);

      // Perifocal coordinates
      // Note: For b < 0, we negate y_pf to create the mirror-image hyperbola
      const x_pf = A * (e - cosh_F);
      const y_pf = s * A * Math.sqrt(e * e - 1) * sinh_F;

      // Time from periapsis
      const t = Math.sqrt((A * A * A) / mu) * (e * sinh_F - F);

      // Rotate to planet frame
      const x_P = cos_omega * x_pf - sin_omega * y_pf;
      const y_P = sin_omega * x_pf + cos_omega * y_pf;

      // Distance from planet
      const r = A * (e * cosh_F - 1);

      // Velocity in perifocal frame
      const v_x_pf = Math.sqrt(mu / A) * sinh_F / (e * cosh_F - 1);
      const v_y_pf = s * Math.sqrt(mu / A) * Math.sqrt(e * e - 1) * cosh_F / (e * cosh_F - 1);

      // Rotate velocity to planet frame
      const u_x = cos_omega * v_x_pf - sin_omega * v_y_pf;
      const u_y = sin_omega * v_x_pf + cos_omega * v_y_pf;

      // Speed check (should equal U)
      const u_mag = Math.sqrt(u_x * u_x + u_y * u_y);

      points.push({
        F, t, r,
        x_P, y_P,
        u_x, u_y, u_mag
      });
    }

    // Sort by time to ensure correct ordering
    points.sort((a, b) => a.t - b.t);

    return points;
  }

  /**
   * § 6: Compute sun-frame positions from planet-frame trajectory
   */
  computeSunFrame(trajectory: TrajectoryPoint[], v0: number) {
    return trajectory.map(point => ({
      ...point,
      // § 6: Sun frame position
      x_S: v0 * point.t + point.x_P,
      y_S: point.y_P,
      // Sun frame velocity
      v_x: v0 + point.u_x,
      v_y: point.u_y
    }));
  }

  /**
   * Validation: Check trajectory invariants
   * Verifies that speed remains constant in planet frame
   */
  validateTrajectory(trajectory: TrajectoryPoint[], U: number): {
    maxSpeedDeviation: number;
    maxRelativeDeviation: number;
    isValid: boolean;
  } {
    // Check that |u(t)| = U everywhere
    const speeds = trajectory.map(p => p.u_mag);
    const maxDev = Math.max(...speeds.map(s => Math.abs(s - U)));
    const maxRelDev = maxDev / U;

    return {
      maxSpeedDeviation: maxDev,
      maxRelativeDeviation: maxRelDev,
      isValid: maxRelDev < 1e-6 // Relative error < 1 ppm
    };
  }

  /**
   * Check for warnings and errors
   */
  private checkWarnings(U: number, rp: number, _Rpl: number | undefined, delta: number): Warning[] {
    const warnings: Warning[] = [];

    if (U < 0.1) {
      warnings.push({ type: 'warning', msg: 'U is very small - scatter may be extreme' });
    }

    if (rp <= 0) {
      warnings.push({ type: 'error', msg: 'Invalid: r_p ≤ 0' });
    }

    if (delta > Math.PI * 0.95) {
      warnings.push({ type: 'warning', msg: 'Near head-on collision (δ ≈ 180°)' });
    }

    if (delta < 0.01) {
      warnings.push({ type: 'info', msg: 'Nearly straight path (δ ≈ 0°)' });
    }

    return warnings;
  }
}
